import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { toast } from 'react-hot-toast';
import {
  CreateSpecialPaymentDto,
  specialPaymentsApi,
  handleApiError
} from '../../services/specialPaymentsService';

interface CreateSpecialPaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  clientId: number;
  clientName: string;
}

const ModalOverlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: ${props => props.$isOpen ? 'flex' : 'none'};
  justify-content: center;
  align-items: center;
  z-index: 1000;
  direction: rtl;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-heavy);
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  direction: rtl;
`;

const ModalHeader = styled.div`
  padding: 24px;
  border-bottom: 1px solid var(--border-color);
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: var(--text-primary);
`;

const ClientBadge = styled.span`
  display: inline-block;
  padding: 4px 12px;
  background: var(--primary-light);
  color: var(--primary-color);
  border-radius: var(--border-radius-md);
  font-size: 12px;
  font-weight: 600;
  margin-right: 12px;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: var(--text-secondary);
  padding: 4px;

  &:hover {
    color: var(--text-primary);
  }
`;

const ModalBody = styled.div`
  padding: 24px;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: var(--text-primary);
  font-size: 14px;
`;

const RequiredStar = styled.span`
  color: var(--error-color);
  margin-right: 4px;
`;

const Input = styled.input<{ hasError?: boolean }>`
  width: 100%;
  padding: 12px;
  border: 1px solid ${props => props.hasError ? 'var(--error-color)' : 'var(--border-color)'};
  border-radius: var(--border-radius-md);
  font-size: 14px;
  color: var(--text-primary);
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${props => props.hasError ? 'var(--error-color)' : 'var(--primary-color)'};
    box-shadow: 0 0 0 2px ${props => props.hasError ? 'var(--error-light)' : 'var(--primary-light)'};
  }
`;

const TextArea = styled.textarea<{ hasError?: boolean }>`
  width: 100%;
  padding: 12px;
  border: 1px solid ${props => props.hasError ? 'var(--error-color)' : 'var(--border-color)'};
  border-radius: var(--border-radius-md);
  font-size: 14px;
  color: var(--text-primary);
  min-height: 80px;
  resize: vertical;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${props => props.hasError ? 'var(--error-color)' : 'var(--primary-color)'};
    box-shadow: 0 0 0 2px ${props => props.hasError ? 'var(--error-light)' : 'var(--primary-light)'};
  }
`;

const ErrorMessage = styled.div`
  color: var(--error-color);
  font-size: 12px;
  margin-top: 4px;
  font-weight: 500;
`;

const HelperText = styled.div`
  color: var(--text-secondary);
  font-size: 12px;
  margin-top: 4px;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 24px;
  border-top: 1px solid var(--border-color);
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  background: ${props => props.variant === 'secondary' ? 'var(--border-color)' : 'var(--primary-color)'};
  color: ${props => props.variant === 'secondary' ? 'var(--text-primary)' : 'white'};
  border: none;
  padding: 12px 24px;
  border-radius: var(--border-radius-md);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CreateSpecialPaymentModal: React.FC<CreateSpecialPaymentModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  clientId,
  clientName
}) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<CreateSpecialPaymentDto>({
    clientId: clientId,
    amount: 0,
    description: '',
    comments: ''
  });
  const [errors, setErrors] = useState<{[key: string]: string}>({});

  useEffect(() => {
    if (isOpen) {
      // Reset form when modal opens
      setFormData({
        clientId: clientId,
        amount: 0,
        description: '',
        comments: ''
      });
      setErrors({});
    }
  }, [isOpen, clientId]);

  const validateForm = (): boolean => {
    const newErrors: {[key: string]: string} = {};

    if (!formData.description || formData.description.trim() === '') {
      newErrors.description = 'יש להזין תיאור התשלום';
    } else if (formData.description.length > 500) {
      newErrors.description = 'התיאור ארוך מדי (מקסימום 500 תווים)';
    }

    if (!formData.amount || formData.amount <= 0) {
      newErrors.amount = 'יש להזין סכום תקין (גדול מאפס)';
    }

    if (formData.comments && formData.comments.length > 1000) {
      newErrors.comments = 'ההערות ארוכות מדי (מקסימום 1000 תווים)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      await specialPaymentsApi.create(formData);
      onSuccess();
      onClose();
    } catch (error) {
      toast.error(handleApiError(error));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: keyof CreateSpecialPaymentDto, value: string | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  return (
    <ModalOverlay $isOpen={isOpen}>
      <ModalContent>
        <form onSubmit={handleSubmit}>
          <ModalHeader>
            <div>
              <ModalTitle>
                הוסף תשלום מיוחד
                <ClientBadge>{clientName}</ClientBadge>
              </ModalTitle>
            </div>
            <CloseButton onClick={onClose} type="button">×</CloseButton>
          </ModalHeader>

          <ModalBody>
            <FormGroup>
              <Label>
                <RequiredStar>*</RequiredStar>
                תיאור התשלום
              </Label>
              <Input
                type="text"
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                hasError={!!errors.description}
                placeholder="לדוגמה: שכר טרחה עורך דין, חוות דעת רפואית, וכו'"
                maxLength={500}
              />
              {errors.description && <ErrorMessage>{errors.description}</ErrorMessage>}
              <HelperText>{formData.description.length}/500 תווים</HelperText>
            </FormGroup>

            <FormGroup>
              <Label>
                <RequiredStar>*</RequiredStar>
                סכום
              </Label>
              <Input
                type="number"
                value={formData.amount || ''}
                onChange={(e) => handleChange('amount', parseFloat(e.target.value) || 0)}
                hasError={!!errors.amount}
                placeholder="0.00"
                step="0.01"
                min="0"
              />
              {errors.amount && <ErrorMessage>{errors.amount}</ErrorMessage>}
              <HelperText>הזן את הסכום בשקלים</HelperText>
            </FormGroup>

            <FormGroup>
              <Label>הערות (אופציונלי)</Label>
              <TextArea
                value={formData.comments || ''}
                onChange={(e) => handleChange('comments', e.target.value)}
                hasError={!!errors.comments}
                placeholder="הערות או פרטים נוספים..."
                maxLength={1000}
              />
              {errors.comments && <ErrorMessage>{errors.comments}</ErrorMessage>}
              <HelperText>{(formData.comments || '').length}/1000 תווים</HelperText>
            </FormGroup>
          </ModalBody>

          <ButtonContainer>
            <Button type="button" variant="secondary" onClick={onClose} disabled={loading}>
              ביטול
            </Button>
            <Button type="submit" variant="primary" disabled={loading}>
              {loading ? 'שומר...' : 'שמור תשלום'}
            </Button>
          </ButtonContainer>
        </form>
      </ModalContent>
    </ModalOverlay>
  );
};

export default CreateSpecialPaymentModal;
